import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';

// Variables de style
const COLORS = {
  primary: '#3b82f6',
  secondary: '#1e40af',
  accent: '#60a5fa',
  light: '#f0f9ff',
  dark: '#1e3a8a',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  text: '#1e293b',
  lightText: '#64748b',
  background: '#f8fafc',
  white: '#ffffff',
  border: '#e2e8f0',
  cardBackground: '#ffffff',
  shadow: 'rgba(0, 0, 0, 0.1)',
};

const API_URL = 'https://api.autocasses.com';

// Fonction utilitaire pour formater les prix
const formatPriceFCFA = (price) => {
  return `${parseInt(price).toLocaleString('fr-FR')} FCFA`;
};

// Fonction pour calculer le temps restant
const calculateTimeLeft = (auction) => {
  if (!auction || !auction.date_enchere || !auction.temps) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  
  const endDate = new Date(auction.date_enchere);
  endDate.setHours(endDate.getHours() + parseInt(auction.temps));
  
  const now = new Date();
  const diff = endDate - now;
  
  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  }
  
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  
  return { days, hours, minutes, seconds, expired: false, endDate };
};

// Composant de carte d'enchère amélioré pour web
const AuctionCard = React.memo(({ item, onPlaceBid, onViewDetails }) => {
  const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(item));
  const [imageError, setImageError] = useState(false);
  const intervalRef = useRef(null);
  
  useEffect(() => {
    if (!timeLeft.expired) {
      intervalRef.current = setInterval(() => {
        const newTimeLeft = calculateTimeLeft(item);
        setTimeLeft(newTimeLeft);
        
        if (newTimeLeft.expired) {
          clearInterval(intervalRef.current);
        }
      }, 1000);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [item]);
  
  const getCurrentPrice = () => {
    if (item.encheres && item.encheres.length > 0) {
      const sortedBids = [...item.encheres].sort((a, b) => b.montant - a.montant);
      return sortedBids[0].montant;
    }
    return item.prix_enchere;
  };
  
  const currentPrice = getCurrentPrice();
  const isAuctionActive = !timeLeft.expired;
  const hasPhotos = item.photos && item.photos.length > 0;
  const bidCount = item.encheres?.length || 0;

  const getTimeRemainingDisplay = () => {
    if (timeLeft.expired) return 'Terminée';
    if (timeLeft.days > 0) return `${timeLeft.days}j ${timeLeft.hours}h`;
    if (timeLeft.hours > 0) return `${timeLeft.hours}h ${timeLeft.minutes}m`;
    return `${timeLeft.minutes}m ${timeLeft.seconds}s`;
  };

  const getTimeColor = () => {
    if (timeLeft.expired) return COLORS.lightText;
    if (timeLeft.days === 0 && timeLeft.hours < 1) return COLORS.danger;
    if (timeLeft.days === 0 && timeLeft.hours < 6) return COLORS.warning;
    return COLORS.success;
  };

  return (
    <div 
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer"
      onClick={() => onViewDetails(item)}
    >
      {/* Image container */}
      <div className="relative h-56 overflow-hidden">
        {hasPhotos && !imageError ? (
          <img 
            src={item.photos[0].photo_url} 
            alt={item.titre}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
            <Icon name="Car" size={48} color={COLORS.lightText} />
          </div>
        )}
        
        {/* Badges superposés */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold ${
            isAuctionActive ? 'bg-blue-500 text-white' : 'bg-slate-500 text-white'
          }`}>
            <Icon name="Gavel" size={12} color="white" className="mr-1.5" />
            <span>{isAuctionActive ? 'Enchère en cours' : 'Terminée'}</span>
          </div>
        </div>
        
        {/* Compteur de photos */}
        {hasPhotos && item.photos.length > 1 && (
          <div className="absolute bottom-4 right-4 inline-flex items-center bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
            <Icon name="Camera" size={12} color="white" className="mr-1.5" />
            <span>{item.photos.length} photos</span>
          </div>
        )}
        
        {/* Timer overlay */}
        <div className="absolute top-4 right-4">
          <div 
            className="flex items-center px-3 py-2 rounded-lg text-sm font-bold text-white shadow-lg"
            style={{ backgroundColor: getTimeColor() }}
          >
            <Icon name="Clock" size={14} color="white" className="mr-2" />
            <span>{getTimeRemainingDisplay()}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <h3 className="font-bold text-lg text-slate-900 mb-3 line-clamp-2 group-hover:text-blue-600 transition-colors">
          {item.titre}
        </h3>
        
        {/* Location */}
        <div className="flex items-center text-slate-600 mb-4">
          <Icon name="MapPin" size={14} color={COLORS.lightText} className="mr-2" />
          <span className="text-sm">{item.city || 'Localisation non spécifiée'}</span>
        </div>
        
        {/* Price section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-600">
              {bidCount > 0 ? 'Offre actuelle' : 'Prix de départ'}
            </span>
            <div className="flex items-center text-sm text-slate-600">
              <Icon name="Users" size={12} color={COLORS.lightText} className="mr-1" />
              <span>{bidCount} offre{bidCount !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="flex items-center">
            <span className="text-2xl font-bold text-blue-700">
              {formatPriceFCFA(currentPrice)}
            </span>
            {bidCount > 0 && (
              <span className="ml-3 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                +{Math.round((currentPrice - item.prix_enchere) / item.prix_enchere * 100)}%
              </span>
            )}
          </div>
        </div>
        
        {/* Action button */}
        <button 
          className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${
            isAuctionActive 
              ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-md hover:shadow-lg' 
              : 'bg-slate-200 text-slate-500 cursor-not-allowed'
          }`}
          onClick={(e) => {
            e.stopPropagation();
            if (isAuctionActive) {
              onPlaceBid(item);
            }
          }}
          disabled={!isAuctionActive}
        >
          {isAuctionActive ? (
            <>
              <Icon name="TrendingUp" size={18} color="white" className="mr-2" />
              <span>Faire une offre</span>
            </>
          ) : (
            <>
              <Icon name="Lock" size={18} color={COLORS.lightText} className="mr-2" />
              <span>Enchère terminée</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
});

// Composant principal amélioré pour web
const Encheres = () => {
  const navigate = useNavigate();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ active: 0, expired: 0, total: 0 });
  const [filter, setFilter] = useState('all'); // all, active, expired
  const [sortBy, setSortBy] = useState('time'); // time, price, bids
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    fetchAuctions();
  }, []);

  const fetchAuctions = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`${API_URL}/api/produit/all`);
      
      if (response.data.success) {
        let activeAuctions = response.data.data.filter(item => item.enchere !== 0);
        
        // Appliquer le tri initial
        const sortedAuctions = sortAuctions(activeAuctions);
        setAuctions(sortedAuctions);
        
        // Calculer les stats
        const active = activeAuctions.filter(a => !calculateTimeLeft(a).expired).length;
        const expired = activeAuctions.filter(a => calculateTimeLeft(a).expired).length;
        setStats({ active, expired, total: activeAuctions.length });
      }
    } catch (err) {
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const sortAuctions = (auctionsList) => {
    const sorted = [...auctionsList];
    
    switch(sortBy) {
      case 'time':
        return sorted.sort((a, b) => {
          const timeLeftA = calculateTimeLeft(a);
          const timeLeftB = calculateTimeLeft(b);
          
          // Les enchères actives en premier
          if (timeLeftA.expired && !timeLeftB.expired) return 1;
          if (!timeLeftA.expired && timeLeftB.expired) return -1;
          
          // Si les deux sont terminées, les plus récentes d'abord
          if (timeLeftA.expired && timeLeftB.expired) {
            return new Date(b.date_enchere) - new Date(a.date_enchere);
          }
          
          // Si les deux sont actives, celles qui se terminent en premier d'abord
          const endDateA = new Date(a.date_enchere);
          endDateA.setHours(endDateA.getHours() + parseInt(a.temps));
          
          const endDateB = new Date(b.date_enchere);
          endDateB.setHours(endDateB.getHours() + parseInt(b.temps));
          
          return endDateA - endDateB;
        });
        
      case 'price':
        return sorted.sort((a, b) => {
          const priceA = getCurrentPrice(a);
          const priceB = getCurrentPrice(b);
          return priceB - priceA;
        });
        
      case 'bids':
        return sorted.sort((a, b) => {
          const bidsA = a.encheres?.length || 0;
          const bidsB = b.encheres?.length || 0;
          return bidsB - bidsA;
        });
        
      default:
        return sorted;
    }
  };

  const getCurrentPrice = (auction) => {
    if (auction.encheres && auction.encheres.length > 0) {
      const sortedBids = [...auction.encheres].sort((a, b) => b.montant - a.montant);
      return sortedBids[0].montant;
    }
    return auction.prix_enchere;
  };

  const filterAuctions = (auctionsList) => {
    let filtered = auctionsList;
    
    // Filtrer par statut
    if (filter === 'active') {
      filtered = filtered.filter(a => !calculateTimeLeft(a).expired);
    } else if (filter === 'expired') {
      filtered = filtered.filter(a => calculateTimeLeft(a).expired);
    }
    
    // Filtrer par recherche
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a => 
        a.titre.toLowerCase().includes(query) ||
        (a.description && a.description.toLowerCase().includes(query)) ||
        (a.city && a.city.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  };

  const handleViewDetails = (auction) => {
    navigate(`/details-enchere/${auction.id}`, { state: { auction } });
  };

  const handlePlaceBid = (auction) => {
    const timeLeft = calculateTimeLeft(auction);
    const isActive = !timeLeft.expired;
    
    if (!isActive) {
      alert('Cette enchère est déjà terminée.');
      return;
    }
    
    navigate(`/auction/${auction.id}`, { state: { auction, placeBid: true } });
  };

  const handleRefresh = () => {
    fetchAuctions();
  };

  const handleCreateAuction = () => {
    navigate('/create-auction');
  };

  // Appliquer filtres et tri
  const displayedAuctions = sortAuctions(filterAuctions(auctions));

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500 mb-6"></div>
          <p className="text-xl text-slate-700 font-medium">Chargement des enchères...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header />
      
      {/* En-tête principal */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-10">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Icon name="Gavel" size={24} color="#3b82f6" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900">
                Enchères Automobiles
              </h1>
            </div>
            <p className="text-lg text-slate-600 mt-2">
              Trouvez les meilleures opportunités et participez à nos enchères exclusives
            </p>
          </div>
        </div>

       

        {/* Filtres et recherche */}
        <div className="mb-10">
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            {/* Barre de recherche */}
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Rechercher une pièce, un modèle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white border border-slate-300 rounded-xl px-6 py-3.5 pl-12 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700 placeholder-slate-500"
                />
                <Icon 
                  name="Search" 
                  size={20} 
                  color="#64748b" 
                  className="absolute left-4 top-1/2 transform -translate-y-1/2" 
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  >
                    <Icon name="X" size={20} color="#64748b" />
                  </button>
                )}
              </div>
            </div>

            {/* Filtres */}
            <div className="flex flex-col sm:flex-row gap-3">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="bg-white border border-slate-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-slate-700 font-medium"
              >
                <option value="time">Trier par: Temps restant</option>
                <option value="price">Trier par: Prix</option>
                <option value="bids">Trier par: Nombre d'offres</option>
              </select>
              
              <button
                onClick={handleRefresh}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg inline-flex items-center justify-center"
              >
                <Icon name="RefreshCw" size={18} color="white" className="mr-2" />
                <span>Actualiser</span>
              </button>
            </div>
          </div>

          {/* Filtres par statut */}
          <div className="flex flex-wrap items-center gap-4 p-6 bg-white rounded-2xl shadow-sm border border-slate-200">
            <span className="font-semibold text-slate-700">Filtrer par statut :</span>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filter === 'all'
                    ? 'bg-blue-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Toutes les enchères
              </button>
              <button
                onClick={() => setFilter('active')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filter === 'active'
                    ? 'bg-emerald-500 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Enchères actives
              </button>
              <button
                onClick={() => setFilter('expired')}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  filter === 'expired'
                    ? 'bg-slate-700 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                Enchères terminées
              </button>
            </div>
          </div>
        </div>

        {/* Liste des enchères */}
        {displayedAuctions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-slate-200">
            <div className="bg-slate-50 p-8 rounded-full mb-8">
              <Icon name="Gavel" size={80} color="#cbd5e1" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              Aucune enchère trouvée
            </h2>
            <p className="text-slate-600 text-center mb-8 max-w-md">
              {searchQuery || filter !== 'all'
                ? 'Aucune enchère ne correspond à vos critères de recherche.'
                : 'Il n\'y a actuellement aucune enchère disponible.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilter('all');
                }}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Icon name="Filter" size={18} color="white" className="inline mr-2" />
                Réinitialiser les filtres
              </button>
              <button
                onClick={handleRefresh}
                className="border-2 border-blue-500 text-blue-500 hover:border-blue-600 hover:text-blue-600 px-8 py-3 rounded-xl font-semibold transition-all duration-300"
              >
                <Icon name="RefreshCw" size={18} color="currentColor" className="inline mr-2" />
                Actualiser
              </button>
            </div>
          </div>
        ) : (
          <div className="mb-10">
            {/* Compteur de résultats */}
            <div className="mb-6 flex items-center justify-between">
              <p className="text-slate-700">
                <span className="font-bold text-blue-600">{displayedAuctions.length}</span> 
                <span className="mx-2">enchères trouvées</span>
              </p>
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-emerald-600">{stats.active}</span> actives • 
                <span className="font-semibold text-slate-600 ml-2">{stats.expired}</span> terminées
              </div>
            </div>

            {/* Grille des enchères - 4 par ligne */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {displayedAuctions.map((auction, index) => (
                <AuctionCard
                  key={auction.id}
                  item={auction}
                  onPlaceBid={handlePlaceBid}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          </div>
        )}

        {/* Section d'information */}
        {auctions.length > 0 && (
          <div className="mt-16 pt-12 border-t border-slate-200">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Comment fonctionnent nos enchères ?
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Des enchères transparentes et sécurisées pour acheter et vendre des pièces automobiles
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="bg-blue-100 p-4 rounded-2xl inline-block mb-6">
                  <Icon name="Eye" size={28} color="#3b82f6" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Enchères transparentes</h3>
                <p className="text-slate-600 leading-relaxed">
                  Toutes les offres sont visibles en temps réel. Suivez l'évolution des prix et faites votre offre au meilleur moment.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="bg-emerald-100 p-4 rounded-2xl inline-block mb-6">
                  <Icon name="ShieldCheck" size={28} color="#10b981" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Sécurité garantie</h3>
                <p className="text-slate-600 leading-relaxed">
                  Votre argent est protégé jusqu'à la livraison. Notre système de garantie assure des transactions sécurisées.
                </p>
              </div>
              
              <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
                <div className="bg-amber-100 p-4 rounded-2xl inline-block mb-6">
                  <Icon name="Clock" size={28} color="#f59e0b" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Délais stricts</h3>
                <p className="text-slate-600 leading-relaxed">
                  Les enchères ont des durées définies. Soyez réactif et ne manquez pas l'opportunité de faire une bonne affaire.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bouton retour en haut */}
      {auctions.length > 8 && (
        <button 
          className="fixed bottom-8 right-8 w-12 h-12 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <Icon name="ChevronUp" size={20} color="white" />
        </button>
      )}

      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .grid > div {
          animation: fadeInUp 0.3s ease-out forwards;
          opacity: 0;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .grid > div:nth-child(n) {
          animation-delay: calc(var(--i, 0) * 0.05s);
        }
      `}</style>
    </div>
  );
};

export default Encheres;